import { Platform } from 'react-native';

/**
 * Platform detection results
 */
export type PlatformType = 'native' | 'web';
export type PlatformOS = 'android' | 'ios' | 'web';
export type WebBrowser = 'android' | 'ios' | 'other';

export interface PlatformInfo {
  /** The platform type (native or web) */
  type: PlatformType;
  /** The operating system */
  os: PlatformOS;
  /** If running on web, the detected browser/device type */
  webBrowser: WebBrowser | undefined;
  /** If running on web, the user agent string */
  userAgent: string | undefined;
  /** Whether the app is running in a mobile browser */
  isMobileWeb: boolean;
  /** Whether the app is running natively on mobile */
  isNativeMobile: boolean;
  /** Whether the app is running on any mobile platform */
  isMobile: boolean;
  /** Whether the app is running on web (any browser) */
  isWeb: boolean;
  /** Whether the app is running on Android (native or web) */
  isAndroid: boolean;
  /** Whether the app is running on iOS (native or web) */
  isIOS: boolean;
}

/**
 * Detects the web browser type based on user agent
 */
function detectWebBrowser(userAgent: string): WebBrowser {
  // Check for Android devices
  if (/Android/i.test(userAgent)) {
    return 'android';
  }
  
  // Check for iOS devices (iPhone, iPad, iPod)
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'ios';
  }
  
  // Default to other for desktop browsers or unknown devices
  return 'other';
}

/**
 * Detects if the device is mobile based on user agent
 */
function isMobileDevice(userAgent: string): boolean {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
}

/**
 * Comprehensive platform detection function
 * 
 * @returns PlatformInfo object with detailed platform information
 * 
 * @example
 * ```typescript
 * const platform = getPlatformInfo();
 * 
 * if (platform.isNativeMobile) {
 *   // Running on native mobile app
 *   console.log(`Native ${platform.os} app`);
 * } else if (platform.isWeb) {
 *   // Running on web
 *   console.log(`Web browser on ${platform.webBrowser}`);
 * }
 * ```
 */
export function getPlatformInfo(): PlatformInfo {
  const isWeb = Platform.OS === 'web';
  const isAndroid = Platform.OS === 'android';
  const isIOS = Platform.OS === 'ios';
  
  let webBrowser: WebBrowser | undefined;
  let userAgent: string | undefined;
  let isMobileWeb = false;
  
  if (isWeb && typeof window !== 'undefined' && window.navigator) {
    userAgent = window.navigator.userAgent;
    webBrowser = detectWebBrowser(userAgent);
    isMobileWeb = isMobileDevice(userAgent);
  }
  
  const isNativeMobile = isAndroid || isIOS;
  const isMobile = isNativeMobile || isMobileWeb;
  
  return {
    type: isWeb ? 'web' : 'native',
    os: isWeb ? 'web' : (Platform.OS as 'android' | 'ios'),
    webBrowser,
    userAgent,
    isMobileWeb,
    isNativeMobile,
    isMobile,
    isWeb,
    isAndroid: isAndroid || (isWeb && webBrowser === 'android'),
    isIOS: isIOS || (isWeb && webBrowser === 'ios'),
  };
}

/**
 * Convenience function to check if running on native mobile
 */
export function isNativeMobile(): boolean {
  return getPlatformInfo().isNativeMobile;
}

/**
 * Convenience function to check if running on web
 */
export function isWeb(): boolean {
  return getPlatformInfo().isWeb;
}

/**
 * Convenience function to check if running on mobile (native or web)
 */
export function isMobile(): boolean {
  return getPlatformInfo().isMobile;
}

/**
 * Convenience function to check if running on Android (native or web)
 */
export function isAndroid(): boolean {
  return getPlatformInfo().isAndroid;
}

/**
 * Convenience function to check if running on iOS (native or web)
 */
export function isIOS(): boolean {
  return getPlatformInfo().isIOS;
}

/**
 * Convenience function to check if running on mobile web
 */
export function isMobileWeb(): boolean {
  return getPlatformInfo().isMobileWeb;
}

/**
 * Convenience function to check if running on web with Android browser
 */
export function isWebOnAndroid(): boolean {
  const info = getPlatformInfo();
  return info.isWeb && info.webBrowser === 'android';
}

/**
 * Convenience function to check if running on web with iOS browser
 */
export function isWebOnIOS(): boolean {
  const info = getPlatformInfo();
  return info.isWeb && info.webBrowser === 'ios';
}

/**
 * Convenience function to check if running on other web browsers (desktop, etc.)
 */
export function isWebOnOther(): boolean {
  const info = getPlatformInfo();
  return info.isWeb && info.webBrowser === 'other';
}

/**
 * Get a human-readable platform description
 */
export function getPlatformDescription(): string {
  const info = getPlatformInfo();
  
  if (info.isNativeMobile) {
    return `Native ${info.os} app`;
  } else if (info.isWeb) {
    if (info.webBrowser === 'android') {
      return 'Web browser on Android device';
    } else if (info.webBrowser === 'ios') {
      return 'Web browser on iOS device';
    } else {
      return 'Web browser on desktop/other device';
    }
  }
  
  return 'Unknown platform';
}
